import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, Download, History, 
  User, AlertTriangle, Clock, FileText,
  Check, X, ChevronDown, ArrowUpRight 
} from 'lucide-react';

// Constants
const API_BASE_URL = 'https://ior.org/risk-register/web/api';

const ACTION_STYLES = {
  'risk-update': 'bg-blue-100 text-blue-800 border-blue-200',
  'approval': 'bg-green-100 text-green-800 border-green-200',
  'new-risk': 'bg-purple-100 text-purple-800 border-purple-200',
  'rejection': 'bg-red-100 text-red-800 border-red-200'
};

const TIME_RANGE_OPTIONS = [
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: '90days', label: 'Last 90 Days' }
];

// Utility Components
const ActionBadge = ({ type, action }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${ACTION_STYLES[type]}`}>
    {action}
  </span>
);

const ChangeItem = ({ field, from, to }) => (
  <div className="flex items-center space-x-2 text-sm">
    <span className="font-medium capitalize">{field}:</span>
    <span className="text-red-500 line-through">{from}</span>
    <ArrowUpRight className="w-4 h-4 text-gray-400" />
    <span className="text-green-500">{to}</span>
  </div>
);

// Detail View Components
const RiskUpdateDetails = ({ details }) => (
  <div className="space-y-4">
    <div>
      <h3 className="text-sm font-medium text-gray-500 mb-2">Changes Made</h3>
      <div className="space-y-2">
        {details.changes.map((change, index) => (
          <ChangeItem key={index} {...change} />
        ))}
      </div>
    </div>
    {details.notes && (
      <div>
        <h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
        <p className="text-sm text-gray-700">{details.notes}</p>
      </div>
    )}
  </div>
);

const ApprovalDetails = ({ details }) => (
  <div className="space-y-4">
    <div>
      <h3 className="text-sm font-medium text-gray-500 mb-2">Approval Details</h3>
      <div className="space-y-2">
        <div className="flex items-center text-sm">
          <span className="font-medium mr-2">Decision:</span>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            details.decision === 'approved' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {details.decision.toUpperCase()}
          </span>
        </div>
        <div className="text-sm">
          <span className="font-medium">Meeting Reference:</span> {details.meetingRef}
        </div>
        <div className="text-sm">
          <span className="font-medium">Comments:</span> {details.comments}
        </div>
      </div>
    </div>
  </div>
);

const NewRiskDetails = ({ details }) => (
  <div className="space-y-4">
    <div>
      <h3 className="text-sm font-medium text-gray-500 mb-2">Risk Details</h3>
      <div className="space-y-2">
        <div className="text-sm">
          <span className="font-medium">Category:</span> {details.category}
        </div>
        <div className="text-sm">
          <span className="font-medium">Initial Status:</span> {details.initialStatus}
        </div>
        <div className="text-sm">
          <span className="font-medium">Description:</span> {details.description}
        </div>
      </div>
    </div>
  </div>
);

// Filter Controls Component
const FilterControls = ({ 
  searchTerm, 
  onSearchChange,
  filterType,
  onFilterTypeChange,
  filterUser,
  onFilterUserChange,
  timeRange,
  onTimeRangeChange,
  onExport 
}) => (
  <>
    <div className="mb-6 grid grid-cols-4 gap-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Search audit trail..."
          className="pl-10 pr-4 py-2 w-full border rounded-md"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
      <select
        className="px-4 py-2 border rounded-md"
        value={filterType}
        onChange={(e) => onFilterTypeChange(e.target.value)}
      >
        <option value="all">All Actions</option>
        <option value="risk-update">Risk Updates</option>
        <option value="approval">Approvals</option>
        <option value="new-risk">New Risks</option>
      </select>
      <select
        className="px-4 py-2 border rounded-md"
        value={filterUser}
        onChange={(e) => onFilterUserChange(e.target.value)}
      >
        <option value="all">All Users</option>
        <option value="smt">SMT Members</option>
        <option value="gb">GB Members</option>
      </select>
      <select
        className="px-4 py-2 border rounded-md"
        value={timeRange}
        onChange={(e) => onTimeRangeChange(e.target.value)}
      >
        {TIME_RANGE_OPTIONS.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>

    <div className="mb-6 flex justify-end">
      <button 
        onClick={onExport}
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
      >
        <Download className="w-4 h-4 mr-2" />
        Export Audit Log
      </button>
    </div>
  </>
);

// Audit Entry Component
const AuditEntry = ({ entry, isExpanded, onToggle }) => {
  const renderDetails = () => {
    switch (entry.type) {
      case 'risk-update':
        return <RiskUpdateDetails details={entry.details} />;
      case 'approval':
        return <ApprovalDetails details={entry.details} />;
      case 'new-risk':
        return <NewRiskDetails details={entry.details} />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <ActionBadge type={entry.type} action={entry.action} />
            <div>
              <div className="font-medium">{entry.riskTitle}</div>
              <div className="text-sm text-gray-500">
                Risk ID: {entry.riskId}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-sm text-gray-500">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-1" />
                {entry.user} ({entry.userRole})
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {entry.timestamp}
              </div>
            </div>
            <button
              onClick={() => onToggle(entry.id)}
              className="text-gray-400 hover:text-gray-600"
            >
              <ChevronDown className={`w-5 h-5 transform transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`} />
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-4 pt-4 border-t">
            {renderDetails()}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Component
const AuditTrail = () => {
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [timeRange, setTimeRange] = useState('30days');
  const [expandedItem, setExpandedItem] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Effects
  useEffect(() => {
    const fetchAuditTrail = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE_URL}/audit-trail.php?type=${filterType}&user=${filterUser}&timeRange=${timeRange}&search=${searchTerm}`
        );
        const data = await response.json();
        
        if (data.success) {
          setEntries(data.data);
          setError(null);
        } else {
          throw new Error(data.error || 'Failed to fetch audit trail');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      fetchAuditTrail();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterType, filterUser, timeRange]);

  // Handlers
  const handleExport = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/audit-trail.php?type=${filterType}&user=${filterUser}&timeRange=${timeRange}&search=${searchTerm}&format=csv`,
        {
          headers: {
            'Accept': 'text/csv'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export audit trail');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-trail-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message);
    }
  };

  // Render
  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Audit Trail</h1>
        <div className="text-gray-600">
          Track all changes and actions in the risk management system
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      {/* Filters */}
      <FilterControls 
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterType={filterType}
        onFilterTypeChange={setFilterType}
        filterUser={filterUser}
        onFilterUserChange={setFilterUser}
        timeRange={timeRange}
        onTimeRangeChange={setTimeRange}
        onExport={handleExport}
      />

      {/* Audit Trail List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No audit entries found
          </div>
        ) : (
          entries.map(entry => (
            <AuditEntry
              key={entry.id}
              entry={entry}
              isExpanded={expandedItem === entry.id}
              onToggle={(id) => setExpandedItem(expandedItem === id ? null : id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default AuditTrail;
